import { useId } from 'react';
import { X, Users, User, Move, Video } from 'lucide-react';
import { BOOKING_DEFAULT_CONFIG } from '../../engine';
import { useT } from '../../i18n';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

export interface BookingHelpModalProps {
  /** Booking config (opening hours, durations, gap rule, smart allocation, …); merged over defaults. */
  config?: any;
  /** Closes the help modal. */
  onClose: () => void;
}

/** Plain-language booking rules explainer (steps, gap rule, smart allocation, pricing, colours); extracted verbatim from the monolith (legacy/App.tsx). */
export function BookingHelpModal({ config, onClose }: BookingHelpModalProps) {
  const t = useT();
  const cfg = { ...BOOKING_DEFAULT_CONFIG, ...(config || {}) };
  const uid = safeId('booking-help', useId());
  const ids = { title: `${uid}-title` };
  // A `Dialog` (#900): named by its own title, focus moved in, kept inside and
  // given back to the opener, Escape closes as the backdrop does. The inline
  // styles are the hand-built overlay's, moved verbatim.
  return (
    <Dialog
      labelledBy={ids.title}
      onClose={onClose}
      showClose={false}
      backdropClassName=""
      backdropStyle={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(15, 22, 17, 0.55)',
      }}
      className=""
      style={{
        background: '#ffffff',
        maxWidth: 680,
        width: '100%',
        maxHeight: 'calc(100dvh - 100px)',
        marginTop: 80,
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 25px 60px -10px rgba(0,0,0,0.4)',
        border: '1px solid #e7eae3',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '14px 20px',
          borderBottom: '1px solid #e7eae3',
          background: '#fafbf7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <h3 id={ids.title} className="font-display text-lg font-semibold leading-tight">
          {t('booking.help.title')}
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('booking.close')}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: '#4a544c',
            padding: 4,
          }}
        >
          <X size={16} />
        </button>
      </div>
      {/* A KEYBOARD CAN SCROLL IT (#1144, WCAG 2.1.1). The body scrolls and
          holds no control, so Tab went from «Lukk» straight past it and the
          arrow keys had nothing to move: the text below the fold was out of
          reach without a mouse. A named, focusable region takes the arrow
          keys and Page Down; its ring is the app's `:focus-visible` one. */}
      <div
        role="region"
        aria-labelledby={ids.title}
        // A scrollable region must be focusable to be scrolled from a keyboard
        // (2.1.1); it is named by the dialog's title.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        style={{ padding: 24, overflowY: 'auto', flex: '1 1 auto' }}
        className="space-y-5 text-sm leading-relaxed text-ink-1"
      >
        <section>
          <h4 className="font-display text-base font-semibold mb-2 text-emerald-700">
            {t('booking.help.steps.title')}
          </h4>
          <ol className="space-y-2 ml-5 list-decimal text-ink-2">
            <li>
              {t('booking.help.steps.one.before')}{' '}
              <strong>{t('booking.help.steps.one.date')}</strong>.
            </li>
            <li>
              {t('booking.help.steps.two.before')}{' '}
              <strong>{t('booking.help.steps.two.start')}</strong>:{' '}
              {t('booking.help.steps.two.after')}
            </li>
            <li>
              {t('booking.help.steps.three.before')}{' '}
              <strong>{t('booking.help.steps.three.duration')}</strong>{' '}
              {t('booking.help.steps.three.and')}{' '}
              <strong>{t('booking.help.steps.three.court')}</strong>,{' '}
              {t('booking.help.steps.three.after')}
            </li>
          </ol>
        </section>

        <section className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
          <h4 className="font-semibold text-emerald-900 mb-1">{t('booking.help.times.title')}</h4>
          <p className="text-emerald-900/80 text-xs mb-2">
            {t('booking.help.times.before')} <strong>{t('booking.help.times.halfHour')}</strong>{' '}
            {t('booking.help.times.examples', { open: cfg.openTime })}{' '}
            {cfg.allowedDurations && cfg.allowedDurations.length > 0 ? (
              <>
                {t('booking.help.times.durationsPrefix')}{' '}
                <strong>
                  {t('booking.help.times.durationsValue', {
                    list: cfg.allowedDurations.join(', '),
                  })}
                </strong>
              </>
            ) : (
              <>{t('booking.help.times.durationsAny')}</>
            )}
            .
          </p>
          <p className="text-emerald-900/80 text-xs">
            {t('booking.help.times.open')}{' '}
            <strong>
              {cfg.openTime}–{cfg.closeTime}
            </strong>
            .
          </p>
        </section>

        <section>
          <h4 className="font-display text-base font-semibold mb-2 text-emerald-700">
            {t('booking.help.gap.title')}
          </h4>
          <p className="text-ink-2 text-xs mb-3">
            {cfg.bufferMin === 0 ? (
              <>
                {t('booking.help.gap.offBefore')} <strong>{t('booking.help.gap.offStrong')}</strong>
                . {t('booking.help.gap.offAfter')}
              </>
            ) : (
              <>
                {t('booking.help.gap.onBefore')} <strong>{t('booking.help.gap.onZero')}</strong>{' '}
                {t('booking.help.gap.onMiddle')}{' '}
                <strong>{t('booking.help.gap.onMore', { min: String(cfg.bufferMin) })}</strong>.{' '}
                {t('booking.help.gap.onAfter')}
              </>
            )}
          </p>
          {cfg.bufferMin > 0 && (
            <div className="bg-bg-soft/60 rounded-lg p-3 text-xs space-y-2">
              <div className="font-mono text-ink-3 mb-1">{t('booking.help.gap.exampleTitle')}</div>
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-emerald-700 w-12">{t('booking.help.gap.ok')}</span>{' '}
                <span>{t('booking.help.gap.example1')}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-rose w-12">{t('booking.help.gap.rejected')}</span>{' '}
                <span>{t('booking.help.gap.example2')}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-emerald-700 w-12">{t('booking.help.gap.ok')}</span>{' '}
                <span>{t('booking.help.gap.example3')}</span>
              </div>
            </div>
          )}
        </section>

        {cfg.smartAllocation !== false && (
          <section className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
            <h4 className="font-semibold text-emerald-900 mb-1">{t('booking.help.smart.title')}</h4>
            <p className="text-emerald-900/80 text-xs mb-2">
              {t('booking.help.smart.p1Before')} <strong>{t('booking.help.smart.p1Strong')}</strong>
              : {t('booking.help.smart.p1After')}
            </p>
            <p className="text-emerald-900/80 text-xs mb-2">{t('booking.help.smart.p2')}</p>
            <p className="text-emerald-900/80 text-xs">
              {t('booking.help.smart.p3Before')} <strong>{t('booking.help.smart.p3Strong')}</strong>
              . {t('booking.help.smart.p3After')}
            </p>
          </section>
        )}

        <section>
          <h4 className="font-display text-base font-semibold mb-2 text-emerald-700">
            {t('booking.help.multi.title')}
          </h4>
          <p className="text-ink-2 text-xs">{t('booking.help.multi.body')}</p>
        </section>

        <section>
          <h4 className="font-display text-base font-semibold mb-2 text-emerald-700">
            {t('booking.help.price.title')}
          </h4>
          <p className="text-ink-2 text-xs mb-2">
            {t('booking.help.price.p1a')} <strong>{t('booking.help.price.p1Default')}</strong>{' '}
            {t('booking.help.price.p1b')} <strong>{t('booking.help.price.p1Zones')}</strong>{' '}
            {t('booking.help.price.p1c')} <strong>{t('booking.help.price.p1Free')}</strong>{' '}
            {t('booking.help.price.p1d')}
          </p>
          <div className="bg-bg-soft/60 rounded-lg p-3 text-xs space-y-1.5 text-ink-2">
            <div>
              <strong>{t('booking.help.price.defaultLabel')}</strong>{' '}
              {t('booking.help.price.defaultText')}
            </div>
            <div>
              <strong>{t('booking.help.price.zoneLabel')}</strong>{' '}
              {t('booking.help.price.zoneText1')} <em>{t('booking.help.price.zoneReplace')}</em>{' '}
              {t('booking.help.price.zoneText2')} <em>{t('booking.help.price.zoneOnTop')}</em>{' '}
              {t('booking.help.price.zoneText3')}
            </div>
            <div>
              <strong>{t('booking.help.price.holidayLabel')}</strong>{' '}
              {t('booking.help.price.holidayText')}
            </div>
            <div>
              <strong>{t('booking.help.price.rangeLabel')}</strong>{' '}
              {t('booking.help.price.rangeText')}
            </div>
          </div>
          <p className="text-ink-3 text-[11px] mt-2">{t('booking.help.price.example')}</p>
        </section>

        <section>
          <h4 className="font-display text-base font-semibold mb-2 text-emerald-700">
            {t('booking.help.facilities.title')}
          </h4>
          <p className="text-ink-2 text-xs mb-2">{t('booking.help.facilities.lead')}</p>
          <div className="space-y-1.5 text-xs text-ink-2 ml-1">
            <div className="flex items-center gap-2">
              <Users size={14} className="text-ink-3" /> {t('booking.help.facilities.double')}{' '}
              <User size={14} className="text-ink-3 inline" /> {t('booking.help.facilities.single')}
            </div>
            <div className="flex items-center gap-2">
              <Move size={14} className="text-ink-3" /> {t('booking.help.facilities.runoff')}
            </div>
            <div className="flex items-center gap-2">
              <Video size={14} className="text-ink-3" /> {t('booking.help.facilities.streaming')}
            </div>
          </div>
        </section>

        <section>
          <h4 className="font-display text-base font-semibold mb-2 text-emerald-700">
            {t('booking.help.calendar.title')}
          </h4>
          <p className="text-ink-2 text-xs mb-2">
            {t('booking.help.calendar.p1a')} <strong>{t('booking.help.calendar.nowLine')}</strong>{' '}
            {t('booking.help.calendar.p1b')}
          </p>
          <p className="text-ink-2 text-xs mb-2">
            {t('booking.help.calendar.p2a')} <strong>{t('booking.help.calendar.p2Strong')}</strong>
          </p>
          <div className="flex flex-col gap-1.5 text-xs">
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm" style={{ background: '#2563eb' }} />{' '}
              {t('booking.help.calendar.legend.user')}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm" style={{ background: '#9333ea' }} />{' '}
              {t('booking.help.calendar.legend.admin')}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm" style={{ background: '#ea580c' }} />{' '}
              {t('booking.help.calendar.legend.tournament')}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm" style={{ background: '#16a34a' }} />{' '}
              {t('booking.help.calendar.legend.series')}
            </span>
          </div>
        </section>

        <section>
          <h4 className="font-display text-base font-semibold mb-2 text-emerald-700">
            {t('booking.help.recurring.title')}
          </h4>
          <p className="text-ink-2 text-xs mb-2">
            {t('booking.help.recurring.p1a')}{' '}
            <strong>{t('booking.help.recurring.p1Strong')}</strong>{' '}
            {t('booking.help.recurring.p1b')}
          </p>
          <p className="text-ink-2 text-xs">
            {t('booking.help.recurring.p2a')} <strong>{t('booking.help.recurring.p2One')}</strong>{' '}
            {t('booking.help.recurring.p2Or')} <strong>{t('booking.help.recurring.p2All')}</strong>.{' '}
            {t('booking.help.recurring.p2b')}
          </p>
        </section>

        <section>
          <h4 className="font-display text-base font-semibold mb-2 text-emerald-700">
            {t('booking.help.price2.title')}
          </h4>
          <p className="text-ink-2 text-xs">
            {t('booking.help.price2.p1a')} <strong>{t('booking.help.price2.zones')}</strong>{' '}
            {t('booking.help.price2.p1b')}
          </p>
        </section>

        <section>
          <h4 className="font-display text-base font-semibold mb-2 text-emerald-700">
            {t('booking.help.admin.title')}
          </h4>
          <p className="text-ink-2 text-xs">{t('booking.help.admin.body')}</p>
        </section>
      </div>
      <div
        style={{
          padding: '12px 20px',
          borderTop: '1px solid #e7eae3',
          background: '#fafbf7',
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
          flexShrink: 0,
        }}
      >
        <button onClick={onClose} className="btn-lime text-sm">
          {t('booking.close')}
        </button>
      </div>
    </Dialog>
  );
}
